/**
 * ================================================================================
 * AGAPEE JAVASCRIPT UTILITIES
 * ================================================================================
 *
 * Custom JavaScript for the Agapee Paris Shopify theme.
 * All components are wrapped in an IIFE and use 'use strict'.
 *
 * COMPONENTS:
 *
 * 1. Overlay Hover Animation (function)
 *    - Handles opacity and color transitions on hover for grid overlays
 *    - Targets elements with .agp-image_grid__overlay--animate
 *    - Reads data-default-opacity, data-hover-opacity, data-default-color, data-hover-color
 *
 * 2. Parallax Scroll Effect (function)
 *    - Fluid parallax movement for images based on scroll position
 *    - Modes: dynamic-zoom, adaptive-length, clamp
 *    - Respects prefers-reduced-motion
 *    - Targets elements with .agp-image_grid__item--parallax
 *
 * 3. <agp-usp-slider> (Custom Element)
 *    - Horizontal auto-scrolling USP ticker/marquee
 *    - Supports pause-on-hover and reduced motion
 *    - Attributes: data-speed, data-pause-on-hover
 *
 * 4. <agp-accordion> (Custom Element)
 *    - Expand/collapse with smooth height transitions
 *    - Auto-closes siblings (one-open-at-a-time)
 *    - Scroll-on-open with sticky header offset subtraction
 *    - Attributes: data-open-default, data-scroll-on-open, data-scroll-subtract-header
 *
 * 5. <agp-buy-button> (Custom Element)
 *    - Customizable ATC button with dynamic checkout (Shop Pay, Apple Pay, Google Pay)
 *    - Back-in-stock notification via Klaviyo integration
 *    - Max-in-cart detection with custom disabled styling
 *    - Pre-order support
 *    - Listens: variant:change (form), cart:change/cart:refresh/cart:add (document),
 *      theme:loading:start/end (documentElement)
 *    - Dispatches: back-in-stock:open
 *
 * 6. <agp-sticky-atc> (Custom Element)
 *    - Fixed-position add-to-cart bar that appears when main buy button scrolls away
 *    - Supports full and compact layouts (desktop/mobile independently)
 *    - Variant selector sync with main product form
 *    - Max-in-cart, sold-out, pre-order, back-in-stock states
 *    - Deferred form lookup to handle block rendering order
 *    - Hides within last 200px of page
 *    - Moves itself to document.body to escape stacking contexts
 *    - Listens: variant:change (document), cart:change/cart:refresh/cart:add (document)
 *    - Dispatches: cart:refresh, cart:add
 *
 * EVENT ARCHITECTURE:
 * - Theme (Prestige) dispatches cart:change on document.documentElement (bubbles to document)
 * - AgpStickyAtc dispatches cart:refresh and cart:add on document
 * - Both AgpBuyButton and AgpStickyAtc listen on document for all three cart events
 * - variant:change is dispatched on the product form element and bubbles up
 * - AgpStickyAtc listens on document for variant:change, filters by form ID
 *
 * DEPENDENCIES:
 * - Shopify theme (Prestige) for variant:change, cart:change, theme:loading events
 * - Klaviyo onsite.js (optional) for back-in-stock notifications
 * - CSS: assets/agapee.scss (compiled to agapee.css)
 *
 * AUTHOR: Thomas LIMBERT
 * VERSION: 1.3.0
 * ================================================================================
 */

(function() {
    'use strict';

    /**
     * Overlay Hover Animation
     * Handles opacity and color transitions on hover for elements with data attributes
     */
    function initOverlayHover() {
        const overlays = document.querySelectorAll('.agp-image_grid__overlay--animate');
        
        overlays.forEach((overlay) => {
            // Skip if already initialized
            if (overlay.dataset.hoverInitialized) return;
            overlay.dataset.hoverInitialized = 'true';

            const item = overlay.closest('.agp-image_grid__item');
            if (!item) return;

            const defaultOpacity = parseFloat(overlay.dataset.defaultOpacity) || 0;
            const hoverOpacity = parseFloat(overlay.dataset.hoverOpacity) || 0.5;
            const defaultColor = overlay.dataset.defaultColor || '';
            const hoverColor = overlay.dataset.hoverColor || '';

            item.addEventListener('mouseenter', () => {
                overlay.style.opacity = hoverOpacity;
                if (hoverColor) {
                    overlay.style.backgroundColor = hoverColor;
                }
            });

            item.addEventListener('mouseleave', () => {
                overlay.style.opacity = defaultOpacity;
                if (defaultColor) {
                    overlay.style.backgroundColor = defaultColor;
                }
            });
        });
    }

    /**
     * Parallax Scroll Effect
     * Applies fluid parallax movement to images based on scroll position.
     * 
     * Three modes available:
     * - dynamic-zoom: Calculates required zoom level to cover max parallax travel
     * - adaptive-length: Adjusts parallax speed based on element position to stay within bounds
     * - clamp: Stops parallax when image border reaches container (with 16px safety margin)
     */
    function initParallax() {
        const parallaxItems = document.querySelectorAll('.agp-image_grid__item--parallax');
        
        if (parallaxItems.length === 0) return;

        // Check for reduced motion preference
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) return;

        // Base parallax speed multiplier
        const baseParallaxSpeed = 0.08;
        // Safety margin to ensure border never shows
        const safetyMargin = 16;
        // Base scale for CSS (used as minimum in dynamic mode)
        const baseScale = 1.15;

        function updateParallax() {
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;

            parallaxItems.forEach(item => {
                const parallaxType = item.dataset.parallax;
                const parallaxMode = item.dataset.parallaxMode || 'clamp';
                
                if (parallaxType === 'none') return;

                const image = item.querySelector('.agp-image_grid__image');
                if (!image) return;

                const rect = item.getBoundingClientRect();
                const elementSize = parallaxType === 'vertical' ? rect.height : rect.width;

                // Calculate how far the element is from viewport center
                const elementCenter = rect.top + rect.height / 2;
                const viewportCenter = viewportHeight / 2;
                const offsetFromCenter = elementCenter - viewportCenter;

                // Calculate max travel distance (from entering viewport to leaving)
                const maxTravelDistance = viewportHeight + rect.height;

                let scale = baseScale;
                let parallaxOffset = 0;

                if (parallaxMode === 'dynamic-zoom') {
                    // MODE 1: Dynamic Zoom
                    // Calculate the required scale to cover the maximum parallax offset
                    const rawOffset = offsetFromCenter * baseParallaxSpeed;
                    const maxPossibleOffset = (maxTravelDistance / 2) * baseParallaxSpeed;
                    
                    // Required extra size = maxOffset * 2 (for both directions) + safety margin
                    const requiredExtraSize = (maxPossibleOffset * 2) + safetyMargin * 2;
                    const requiredScale = 1 + (requiredExtraSize / elementSize);
                    
                    // Use the larger of base scale or required scale
                    scale = Math.max(baseScale, requiredScale);
                    parallaxOffset = rawOffset;

                } else if (parallaxMode === 'adaptive-length') {
                    // MODE 2: Adaptive Length
                    // Calculate available margin from current scale and adjust parallax speed accordingly
                    const availableMargin = (elementSize * (baseScale - 1) / 2) - safetyMargin;
                    const maxPossibleOffset = (maxTravelDistance / 2) * baseParallaxSpeed;
                    
                    // Adjust speed so max offset fits within available margin
                    const adaptedSpeed = maxPossibleOffset > 0 
                        ? (availableMargin / (maxTravelDistance / 2)) 
                        : baseParallaxSpeed;
                    
                    parallaxOffset = offsetFromCenter * Math.min(adaptedSpeed, baseParallaxSpeed);
                    scale = baseScale;

                } else {
                    // MODE 3: Clamp (default)
                    // Stop parallax when image border would reach container border
                    const maxOffset = (elementSize * (baseScale - 1) / 2) - safetyMargin;
                    const rawOffset = offsetFromCenter * baseParallaxSpeed;
                    
                    // Clamp to safe bounds
                    parallaxOffset = Math.max(-maxOffset, Math.min(maxOffset, rawOffset));
                    scale = baseScale;
                }

                // Apply transform
                if (parallaxType === 'vertical') {
                    image.style.transform = `scale(${scale}) translateY(${parallaxOffset}px)`;
                } else if (parallaxType === 'horizontal') {
                    image.style.transform = `scale(${scale}) translateX(${parallaxOffset}px)`;
                }
            });
        }

        // Direct scroll listener - no throttling for instant response
        window.addEventListener('scroll', updateParallax, { passive: true });
        window.addEventListener('resize', updateParallax, { passive: true });
        
        // Initial update
        updateParallax();
    }

    /**
     * USP Slider Custom Element
     * Handles auto-rotation, slide/fade transitions, arrow and dot navigation.
     */
    class AgpUspSlider extends HTMLElement {
        constructor() {
            super();
            this.currentIndex = 0;
            this.autoplayInterval = null;
            this.progressAnimation = null;
        }

        connectedCallback() {
            this.slidesContainer = this.querySelector('.agp-usp-slider__slides');
            this.slides = this.querySelectorAll('.agp-usp-slider__slide');
            this.dots = this.querySelectorAll('.agp-usp-slider__dot');
            this.prevBtn = this.querySelector('.agp-usp-slider__arrow--prev');
            this.nextBtn = this.querySelector('.agp-usp-slider__arrow--next');
            // Progress bar is now outside the slider, query from parent
            this.progressBar = this.parentElement?.querySelector('.agp-usp-slider__progress-bar');
            
            if (this.slides.length <= 1) return;

            this.autoplay = this.dataset.autoplay === 'true';
            this.speed = parseInt(this.dataset.speed, 10) || 3000;
            this.transition = this.dataset.transition || 'slide';

            this.setupSlides();
            this.bindEvents();
            
            if (this.autoplay) {
                this.startAutoplay();
            }
        }

        disconnectedCallback() {
            this.stopAutoplay();
        }

        setupSlides() {
            const dotColor = this.dots[0]?.style.borderColor || '#000';
            
            if (this.transition === 'fade') {
                // Fade mode: slides are absolutely positioned
                this.slides.forEach((slide, i) => {
                    slide.style.position = i === 0 ? 'relative' : 'absolute';
                    slide.style.opacity = i === 0 ? '1' : '0';
                    slide.style.top = '0';
                    slide.style.left = '0';
                    slide.style.transition = 'opacity 0.4s ease';
                });
            } else {
                // Slide mode: slides in a row
                this.slidesContainer.style.transition = 'transform 0.4s ease';
                this.goToSlide(0, false);
            }
        }

        bindEvents() {
            this.prevBtn?.addEventListener('click', () => {
                this.prev();
                this.resetAutoplay();
            });

            this.nextBtn?.addEventListener('click', () => {
                this.next();
                this.resetAutoplay();
            });

            this.dots.forEach(dot => {
                dot.addEventListener('click', () => {
                    const index = parseInt(dot.dataset.slide, 10);
                    this.goToSlide(index);
                    this.resetAutoplay();
                });
            });

            // Touch swipe support
            let touchStartX = 0;
            let touchStartY = 0;
            let isSwiping = false;

            this.addEventListener('touchstart', (e) => {
                touchStartX = e.touches[0].clientX;
                touchStartY = e.touches[0].clientY;
                isSwiping = false;
            }, { passive: true });

            this.addEventListener('touchmove', (e) => {
                if (!touchStartX) return;
                const dx = e.touches[0].clientX - touchStartX;
                const dy = e.touches[0].clientY - touchStartY;
                // Lock to horizontal swipe if horizontal movement exceeds vertical
                if (!isSwiping && Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
                    isSwiping = true;
                }
                // Prevent vertical scroll while swiping horizontally
                if (isSwiping) {
                    e.preventDefault();
                }
            }, { passive: false });

            this.addEventListener('touchend', (e) => {
                if (!isSwiping) { touchStartX = 0; return; }
                const dx = e.changedTouches[0].clientX - touchStartX;
                const threshold = 50;
                if (dx < -threshold) {
                    this.next();
                    this.resetAutoplay();
                } else if (dx > threshold) {
                    this.prev();
                    this.resetAutoplay();
                }
                touchStartX = 0;
                isSwiping = false;
            }, { passive: true });

            // Pause on hover
            this.addEventListener('mouseenter', () => this.stopAutoplay());
            this.addEventListener('mouseleave', () => {
                if (this.autoplay) this.startAutoplay();
            });
        }

        goToSlide(index, animate = true) {
            // Wrap around
            if (index < 0) index = this.slides.length - 1;
            if (index >= this.slides.length) index = 0;

            const previousIndex = this.currentIndex;
            this.currentIndex = index;

            if (this.transition === 'fade') {
                this.slides.forEach((slide, i) => {
                    slide.style.opacity = i === index ? '1' : '0';
                    slide.style.position = i === index ? 'relative' : 'absolute';
                });
            } else {
                const offset = -index * 100;
                this.slidesContainer.style.transform = `translateX(${offset}%)`;
            }

            // Update dots
            this.updateDots(previousIndex, index);
        }

        updateDots(previousIndex, currentIndex) {
            const dotsContainer = this.querySelector('.agp-usp-slider__dots');
            const activeColor = dotsContainer ? getComputedStyle(dotsContainer).getPropertyValue('--dot-active').trim() || '#000' : '#000';
            const inactiveColor = dotsContainer ? getComputedStyle(dotsContainer).getPropertyValue('--dot-inactive').trim() || 'transparent' : 'transparent';
            const activeBorder = dotsContainer ? getComputedStyle(dotsContainer).getPropertyValue('--dot-border-active').trim() || activeColor : activeColor;
            const inactiveBorder = dotsContainer ? getComputedStyle(dotsContainer).getPropertyValue('--dot-border-inactive').trim() || activeColor : activeColor;
            
            if (this.dots[previousIndex]) {
                this.dots[previousIndex].style.background = inactiveColor;
                this.dots[previousIndex].style.borderColor = inactiveBorder;
                this.dots[previousIndex].classList.remove('agp-usp-slider__dot--active');
            }
            if (this.dots[currentIndex]) {
                this.dots[currentIndex].style.background = activeColor;
                this.dots[currentIndex].style.borderColor = activeBorder;
                this.dots[currentIndex].classList.add('agp-usp-slider__dot--active');
            }
        }

        next() {
            this.goToSlide(this.currentIndex + 1);
        }

        prev() {
            this.goToSlide(this.currentIndex - 1);
        }

        startAutoplay() {
            this.stopAutoplay();
            this.startProgress();
            this.autoplayInterval = setInterval(() => {
                this.next();
                this.startProgress();
            }, this.speed);
        }

        stopAutoplay() {
            if (this.autoplayInterval) {
                clearInterval(this.autoplayInterval);
                this.autoplayInterval = null;
            }
            this.pauseProgress();
        }

        startProgress() {
            if (!this.progressBar) return;
            // Reset progress
            this.progressBar.style.transition = 'none';
            this.progressBar.style.width = '0%';
            // Force reflow
            this.progressBar.offsetWidth;
            // Animate to 100%
            this.progressBar.style.transition = `width ${this.speed}ms linear`;
            this.progressBar.style.width = '100%';
        }

        pauseProgress() {
            if (!this.progressBar) return;
            // Get current width and freeze it
            const computedWidth = getComputedStyle(this.progressBar).width;
            this.progressBar.style.transition = 'none';
            this.progressBar.style.width = computedWidth;
        }

        resetAutoplay() {
            if (this.autoplay) {
                this.startAutoplay();
            }
        }
    }

    // Register custom element
    if (!customElements.get('agp-usp-slider')) {
        customElements.define('agp-usp-slider', AgpUspSlider);
    }

    /**
     * AGP Accordion Component
     * Handles expand/collapse with animated icon
     */
    class AgpAccordion extends HTMLElement {
        constructor() {
            super();
            this.button = null;
            this.panel = null;
            this.isOpen = false;
        }

        connectedCallback() {
            this.button = this.querySelector('.agp-accordion__title');
            this.panel = this.querySelector('.agp-accordion__panel');

            if (this.button && this.panel) {
                this.button.addEventListener('click', this.toggle.bind(this));

                // Handle open by default
                if (this.hasAttribute('data-open-default')) {
                    this.isOpen = true;
                    const content = this.panel.querySelector('.agp-accordion__content');
                    if (content) {
                        this.panel.style.maxHeight = content.scrollHeight + 'px';
                    }
                }
            }
        }

        disconnectedCallback() {
            if (this.button) {
                this.button.removeEventListener('click', this.toggle.bind(this));
            }
        }

        toggle() {
            this.isOpen = !this.isOpen;
            this.button.setAttribute('aria-expanded', this.isOpen);
            this.panel.setAttribute('aria-hidden', !this.isOpen);
            this.classList.toggle('is-open', this.isOpen);
            
            // Mark as interacted to enable CSS animations
            if (!this.classList.contains('has-interacted')) {
                this.classList.add('has-interacted');
            }

            if (this.isOpen) {
                // Close other accordions if this one has close-others attribute
                // Track which panels above us will collapse (for scroll calculation)
                let closingHeightAbove = 0;
                if (this.hasAttribute('data-close-others')) {
                    const allAccordions = document.querySelectorAll('agp-accordion');
                    allAccordions.forEach(accordion => {
                        if (accordion !== this && accordion.isOpen) {
                            // Check if this accordion is above us in the DOM
                            if (this.compareDocumentPosition(accordion) & Node.DOCUMENT_POSITION_PRECEDING) {
                                const panel = accordion.querySelector('.agp-accordion__panel');
                                if (panel) {
                                    closingHeightAbove += panel.scrollHeight;
                                }
                            }
                            accordion.close();
                        }
                    });
                }
                
                const content = this.panel.querySelector('.agp-accordion__content');
                if (content) {
                    this.panel.style.maxHeight = content.scrollHeight + 'px';
                }
                
                // Scroll to accordion title only if closing accordions above
                // will push it out of view (behind sticky header or above viewport)
                if (this.hasAttribute('data-scroll-on-open') && closingHeightAbove > 0) {
                    let offset = 0;
                    if (this.hasAttribute('data-scroll-subtract-header')) {
                        const probe = document.createElement('div');
                        probe.style.cssText = 'position:absolute;top:0;left:0;height:var(--sticky-area-height,0px);pointer-events:none;visibility:hidden;';
                        document.body.appendChild(probe);
                        offset = probe.offsetHeight;
                        probe.remove();
                    }
                    // Current position minus the height that will collapse above us
                    const futureTop = this.getBoundingClientRect().top - closingHeightAbove;
                    if (futureTop < offset) {
                        const scrollTarget = this.getBoundingClientRect().top + window.scrollY - closingHeightAbove - offset;
                        window.scrollTo({ top: scrollTarget, behavior: 'smooth' });
                    }
                }
            } else {
                this.panel.style.maxHeight = '0';
            }
        }
        
        closeOtherAccordions() {
            const allAccordions = document.querySelectorAll('agp-accordion');
            allAccordions.forEach(accordion => {
                if (accordion !== this && accordion.isOpen) {
                    accordion.close();
                }
            });
        }
        
        close() {
            if (!this.isOpen) return;
            
            this.isOpen = false;
            this.button.setAttribute('aria-expanded', false);
            this.panel.setAttribute('aria-hidden', true);
            this.classList.remove('is-open');
            
            // Mark as interacted for animation
            if (!this.classList.contains('has-interacted')) {
                this.classList.add('has-interacted');
            }
            
            this.panel.style.maxHeight = '0';
        }
    }

    // Register accordion custom element
    if (!customElements.get('agp-accordion')) {
        customElements.define('agp-accordion', AgpAccordion);
    }

    /**
     * AGP Buy Button
     * 
     * A fully customizable add-to-cart button with dynamic checkout support
     * (Shop Pay, Apple Pay, Google Pay) and out-of-stock notification integration.
     * 
     * Attributes:
     * - data-section-id: Section identifier
     * - data-url: Product URL for variant fetching
     * - data-atc-text: Add to cart button text
     * - data-preorder-text: Pre-order button text  
     * - data-sold-out-text: Sold out button text
     * - data-notification-text: Notification button text
     * - data-enable-notification: "true" | "false" - Enable back-in-stock notification
     * - data-is-preorder: "true" | "false" - Whether product is pre-order
     * 
     * Events:
     * - Listens to form's `variant:change` event for variant updates
     * - Notification button triggers Klaviyo back-in-stock modal
     */
    class AgpBuyButton extends HTMLElement {
        constructor() {
            super();
            this.formElement = null;
            this.atcWrapper = null;
            this.atcButton = null;
            this.notificationWrapper = null;
            this.notificationButton = null;
            this.dynamicCheckout = null;
            this.variantIdInput = null;
            this.isLoading = false;
            this.cartData = null;
            this.currentVariant = null;
            this.isMaxInCart = false;
            this.variantInventories = {}; // Inventory data passed from Liquid
            
            // Bound event handlers
            this.onVariantChange = this.handleVariantChange.bind(this);
            this.onNotificationClick = this.handleNotificationClick.bind(this);
            this.onLoadingStart = this.startLoading.bind(this);
            this.onLoadingEnd = this.handleLoadingEnd.bind(this);
            this.onCartChange = this.handleCartChange.bind(this);
        }

        connectedCallback() {
            // Get form element from attribute
            const formId = this.getAttribute('form');
            this.formElement = formId ? document.getElementById(formId) : this.querySelector('form');
            
            // Cache DOM elements
            this.atcWrapper = this.querySelector('[data-atc-wrapper]');
            this.atcButton = this.querySelector('[data-atc-button]');
            this.notificationWrapper = this.querySelector('[data-notification-wrapper]');
            this.notificationButton = this.querySelector('[data-notification-button]');
            this.dynamicCheckout = this.querySelector('[data-dynamic-checkout]');
            this.variantIdInput = this.querySelector('[data-variant-id]');
            
            // Listen to variant changes from the main product form
            if (this.formElement) {
                this.formElement.addEventListener('variant:change', this.onVariantChange);
            }
            
            // Add notification button click handler
            if (this.notificationButton) {
                this.notificationButton.addEventListener('click', this.onNotificationClick);
            }
            
            // Listen for theme loading events (dispatched by product-form)
            document.documentElement.addEventListener('theme:loading:start', this.onLoadingStart);
            document.documentElement.addEventListener('theme:loading:end', this.onLoadingEnd);
            
            // Listen for cart changes (theme dispatches cart:change on documentElement which bubbles to document;
            // our own AgpStickyAtc dispatches cart:refresh and cart:add on document)
            document.addEventListener('cart:change', this.onCartChange);
            document.addEventListener('cart:refresh', this.onCartChange);
            document.addEventListener('cart:add', this.onCartChange);
            
            // Also listen on form for variant:add (success) and cart:error
            if (this.formElement) {
                this.formElement.addEventListener('variant:add', this.onLoadingEnd);
                this.formElement.addEventListener('cart:error', this.onLoadingEnd);
            }
            
            // Parse variant inventory data from Liquid (guaranteed to have inventory info)
            try {
                const inventoryData = this.dataset.variantInventories;
                if (inventoryData) {
                    this.variantInventories = JSON.parse(inventoryData);
                }
            } catch (e) {
                console.error('AGP Buy Button: Error parsing variant inventories', e);
            }
            
            // Initialize current variant from data attribute
            this.initializeCurrentVariant();
            
            // Fetch initial cart data and check inventory
            this.fetchCartAndCheckInventory();
        }
        
        /**
         * Initialize current variant from data attribute
         * Uses Liquid-provided inventory data, no need to fetch product.js
         */
        initializeCurrentVariant() {
            const initialVariantId = parseInt(this.dataset.currentVariantId);
            if (!initialVariantId) return;
            
            // Get inventory info from Liquid-provided data
            const inventoryInfo = this.variantInventories[String(initialVariantId)];
            
            // Create a minimal variant object with the data we need
            this.currentVariant = {
                id: initialVariantId,
                inventory_quantity: inventoryInfo?.inventory_quantity ?? 0,
                inventory_policy: inventoryInfo?.inventory_policy ?? 'deny',
                available: inventoryInfo?.available ?? true
            };
        }
        
        /**
         * Get variant with inventory data
         * Uses Liquid-provided inventory data as primary source (always available)
         * @param {Object} variant - The variant object
         * @returns {Object} Variant with inventory data
         */
        getVariantWithInventory(variant) {
            if (!variant) return null;
            
            // Primary source: Liquid-provided inventory data (always has inventory info)
            const inventoryInfo = this.variantInventories[String(variant.id)];
            if (inventoryInfo) {
                return {
                    ...variant,
                    inventory_quantity: inventoryInfo.inventory_quantity,
                    inventory_policy: inventoryInfo.inventory_policy,
                    available: inventoryInfo.available
                };
            }
            
            // Fallback: If variant already has inventory data, use it
            if (typeof variant.inventory_quantity === 'number') {
                return variant;
            }
            
            // Fallback: Look up in cached product data from .js endpoint
            if (this.productData?.variants) {
                const fullVariant = this.productData.variants.find(v => v.id === variant.id);
                if (fullVariant && typeof fullVariant.inventory_quantity === 'number') {
                    return fullVariant;
                }
            }
            
            return variant;
        }

        disconnectedCallback() {
            if (this.formElement) {
                this.formElement.removeEventListener('variant:change', this.onVariantChange);
                this.formElement.removeEventListener('variant:add', this.onLoadingEnd);
                this.formElement.removeEventListener('cart:error', this.onLoadingEnd);
            }
            if (this.notificationButton) {
                this.notificationButton.removeEventListener('click', this.onNotificationClick);
            }
            document.documentElement.removeEventListener('theme:loading:start', this.onLoadingStart);
            document.documentElement.removeEventListener('theme:loading:end', this.onLoadingEnd);
            document.removeEventListener('cart:change', this.onCartChange);
            document.removeEventListener('cart:refresh', this.onCartChange);
            document.removeEventListener('cart:add', this.onCartChange);
        }
        
        /**
         * Fetch cart data and check inventory levels
         */
        async fetchCartAndCheckInventory() {
            try {
                const response = await fetch(`${Shopify.routes.root}cart.js`);
                this.cartData = await response.json();
                this.checkMaxInCart();
            } catch (error) {
                console.error('AGP Buy Button: Error fetching cart', error);
            }
        }
        
        /**
         * Handle cart change events
         */
        handleCartChange(event) {
            // Update cart data from event if available, otherwise refetch
            if (event.detail?.cart) {
                this.cartData = event.detail.cart;
                this.checkMaxInCart();
            } else {
                this.fetchCartAndCheckInventory();
            }
        }
        
        /**
         * Check if current variant has reached max quantity in cart
         */
        checkMaxInCart() {
            if (!this.cartData || !this.currentVariant) return;
            
            // Get variant with full inventory data
            const variant = this.getVariantWithInventory(this.currentVariant);
            if (!variant) return;
            
            const variantId = variant.id;
            const variantInventory = variant.inventory_quantity;
            const inventoryPolicy = variant.inventory_policy;
            
            // If inventory policy is 'continue', they can always add more
            if (inventoryPolicy === 'continue') {
                this.setMaxInCartState(false);
                return;
            }
            
            // If inventory quantity is not available, we can't check
            if (typeof variantInventory !== 'number') {
                return;
            }
            
            // Find this variant in cart
            const cartItem = this.cartData.items?.find(item => item.variant_id === variantId);
            const quantityInCart = cartItem?.quantity || 0;
            
            // Check if quantity in cart >= available inventory
            const isMaxReached = quantityInCart >= variantInventory && variantInventory > 0;
            
            this.setMaxInCartState(isMaxReached);
        }
        
        /**
         * Set max in cart state and update button
         * @param {boolean} isMax - Whether max quantity is reached
         */
        setMaxInCartState(isMax) {
            if (this.isMaxInCart === isMax) return;
            
            this.isMaxInCart = isMax;
            
            if (!this.atcButton) return;
            
            if (isMax) {
                // Disable button and update text
                this.atcButton.disabled = true;
                const textElement = this.atcButton.querySelector('[data-button-text]');
                if (textElement) {
                    textElement.textContent = this.dataset.maxInCartText || 'Max quantity reached';
                }
                
                // Apply custom disabled styling if provided
                this.applyDisabledStyling();
                
                // Hide dynamic checkout
                if (this.dynamicCheckout) {
                    this.dynamicCheckout.style.display = 'none';
                }
            } else {
                // Re-enable button if variant is available
                if (this.currentVariant?.available) {
                    this.atcButton.disabled = false;
                    const isPreorder = this.dataset.isPreorder === 'true';
                    const textElement = this.atcButton.querySelector('[data-button-text]');
                    if (textElement) {
                        textElement.textContent = isPreorder 
                            ? (this.dataset.preorderText || 'Pre-order')
                            : (this.dataset.atcText || 'Add to cart');
                    }
                }
                
                // Remove custom disabled styling
                this.removeDisabledStyling();
                
                // Show dynamic checkout if variant is available
                if (this.dynamicCheckout && this.currentVariant?.available) {
                    this.dynamicCheckout.style.display = '';
                }
            }
        }
        
        /**
         * Apply custom disabled button styling
         */
        applyDisabledStyling() {
            if (!this.atcButton) return;
            
            const disabledBg = this.dataset.disabledBg;
            const disabledText = this.dataset.disabledText;
            
            if (disabledBg) {
                const rgbBg = this.hexToRgb(disabledBg);
                if (rgbBg) {
                    this.atcButton.style.setProperty('--agp-btn-disabled-bg', rgbBg);
                }
            }
            if (disabledText) {
                const rgbText = this.hexToRgb(disabledText);
                if (rgbText) {
                    this.atcButton.style.setProperty('--agp-btn-disabled-text', rgbText);
                }
            }
            
            this.atcButton.classList.add('is-max-in-cart');
            this.classList.add('is-max-in-cart');
        }
        
        /**
         * Convert hex color to RGB values string
         * @param {string} hex - Hex color (e.g., "#ED8A00" or "ED8A00")
         * @returns {string|null} RGB values string (e.g., "237, 138, 0") or null if invalid
         */
        hexToRgb(hex) {
            if (!hex) return null;
            
            // Remove # if present
            hex = hex.replace(/^#/, '');
            
            // Handle shorthand (e.g., "F00" -> "FF0000")
            if (hex.length === 3) {
                hex = hex.split('').map(c => c + c).join('');
            }
            
            if (hex.length !== 6) return null;
            
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            
            if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
            
            return `${r}, ${g}, ${b}`;
        }
        
        /**
         * Remove custom disabled button styling
         */
        removeDisabledStyling() {
            if (!this.atcButton) return;
            
            // Only remove if we applied custom styling
            if (this.dataset.disabledBg) {
                this.atcButton.style.removeProperty('--agp-btn-disabled-bg');
            }
            if (this.dataset.disabledText) {
                this.atcButton.style.removeProperty('--agp-btn-disabled-text');
            }
            
            this.atcButton.classList.remove('is-max-in-cart');
            this.classList.remove('is-max-in-cart');
        }
        
        /**
         * Handle loading end and recheck cart
         */
        handleLoadingEnd() {
            this.stopLoading();
            // Refetch cart after adding to cart
            setTimeout(() => this.fetchCartAndCheckInventory(), 100);
        }
        
        /**
         * Start loading state
         */
        startLoading() {
            if (this.isLoading) return;
            // Only show loading if this form's button was clicked (check aria-busy)
            if (!this.atcButton?.getAttribute('aria-busy')) return;
            
            this.isLoading = true;
            this.classList.add('is-loading');
        }
        
        /**
         * Stop loading state
         */
        stopLoading() {
            if (!this.isLoading) return;
            this.isLoading = false;
            this.classList.remove('is-loading');
        }

        /**
         * Handle variant change events from the product form
         * @param {CustomEvent} event - The variant:change event
         */
        handleVariantChange(event) {
            const variant = event.detail?.variant;
            const enableNotification = this.dataset.enableNotification === 'true';
            const isPreorder = this.dataset.isPreorder === 'true';
            const showPrice = this.dataset.showPrice === 'true';
            
            // Store current variant for inventory checks
            // Use the variant with full inventory data if available
            this.currentVariant = variant ? this.getVariantWithInventory(variant) : null;
            
            // Reset max in cart state when variant changes
            this.isMaxInCart = false;
            this.removeDisabledStyling();
            
            // Update variant ID in the hidden input
            if (this.variantIdInput && variant) {
                this.variantIdInput.value = variant.id;
            }
            
            // Format price if needed
            let priceText = '';
            if (showPrice && variant) {
                priceText = this.formatMoney(variant.price);
            }
            
            if (!variant) {
                // Variant unavailable (no matching variant for selected options)
                this.updateButtonState({
                    isAvailable: false,
                    buttonText: window.themeVariables?.strings?.unavailableButton || 'Unavailable',
                    priceText: '',
                    enableNotification: false // Don't show notification for unavailable variants
                });
            } else if (variant.available) {
                // Variant is in stock
                const text = isPreorder 
                    ? (this.dataset.preorderText || window.themeVariables?.strings?.preOrderButton || 'Pre-order')
                    : (this.dataset.atcText || window.themeVariables?.strings?.addToCartButton || 'Add to cart');
                    
                this.updateButtonState({
                    isAvailable: true,
                    buttonText: text,
                    priceText: priceText,
                    enableNotification: false
                });
                
                // Check max in cart after updating state
                this.checkMaxInCart();
            } else {
                // Variant is out of stock
                if (enableNotification) {
                    this.updateButtonState({
                        isAvailable: false,
                        buttonText: this.dataset.notificationText || 'Notify me when available',
                        priceText: '',
                        enableNotification: true
                    });
                } else {
                    this.updateButtonState({
                        isAvailable: false,
                        buttonText: this.dataset.soldOutText || window.themeVariables?.strings?.soldOutButton || 'Sold out',
                        priceText: priceText,
                        enableNotification: false
                    });
                }
            }
        }
        
        /**
         * Format money value (cents to formatted currency)
         * @param {number} cents - Price in cents
         * @returns {string} Formatted price
         */
        formatMoney(cents) {
            const formatter = new Intl.NumberFormat(Shopify.locale || 'en', {
                style: 'currency',
                currency: Shopify.currency?.active || 'EUR'
            });
            let formatted = formatter.format(cents / 100);
            // Append currency code if theme setting enables it (matches Shopify's money_with_currency filter)
            if (this.dataset.currencyCodeEnabled === 'true') {
                const code = Shopify.currency?.active || 'EUR';
                if (!formatted.includes(code)) {
                    formatted += ' ' + code;
                }
            }
            return formatted;
        }

        /**
         * Update the button state based on variant availability
         * @param {Object} state - The state object
         * @param {boolean} state.isAvailable - Whether the variant is available
         * @param {string} state.buttonText - The button text to display
         * @param {string} state.priceText - The price text to display
         * @param {boolean} state.enableNotification - Whether to show notification button
         */
        updateButtonState({ isAvailable, buttonText, priceText, enableNotification }) {
            // Determine what to show:
            // - If available: show ATC, hide notification
            // - If not available + notification enabled: hide ATC, show notification
            // - If not available + notification disabled: show ATC (disabled with sold out text), hide notification
            const showAtc = isAvailable || !enableNotification;
            const showNotification = !isAvailable && enableNotification;
            
            // Update ATC button visibility and state
            if (this.atcWrapper) {
                this.atcWrapper.style.display = showAtc ? '' : 'none';
            }
            
            if (this.atcButton) {
                this.atcButton.disabled = !isAvailable;
                // Update button text using data-button-text element
                const textElement = this.atcButton.querySelector('[data-button-text]');
                if (textElement) {
                    textElement.textContent = buttonText;
                }
                // Update price using data-button-price element
                const priceElement = this.atcButton.querySelector('[data-button-price]');
                if (priceElement && priceText) {
                    priceElement.textContent = priceText;
                }
            }
            
            // Update notification button visibility
            if (this.notificationWrapper) {
                this.notificationWrapper.style.display = showNotification ? '' : 'none';
            }
            
            // Update dynamic checkout visibility
            if (this.dynamicCheckout) {
                this.dynamicCheckout.style.display = isAvailable ? '' : 'none';
            }
        }

        /**
         * Handle notification button click — redirect to product page.
         * 
         * When inside a quick-buy modal (collection page / product slider),
         * navigates to the product page with the selected variant pre-selected.
         * On the product page itself, Klaviyo's BIS handles the click natively.
         */
        handleNotificationClick() {
            const variantId = this.variantIdInput?.value;
            const productUrl = this.dataset.url;

            // Only redirect when inside a quick-buy modal context
            // (i.e. we're NOT already on the product page)
            const isQuickBuy = !!this.closest('quick-buy-modal, .quick-buy-modal');
            if (isQuickBuy && productUrl) {
                window.location.href = variantId ? `${productUrl}?variant=${variantId}` : productUrl;
            }
        }
    }

    // Register buy button custom element
    if (!customElements.get('agp-buy-button')) {
        customElements.define('agp-buy-button', AgpBuyButton);
    }

    /**
     * AGP Sticky Add to Cart
     * 
     * A sticky add to cart bar that appears when the main ATC button scrolls
     * out of view and hides when the footer comes into view.
     * 
     * Attributes:
     * - data-animation: "fade" | "slide" - Animation type
     * - data-animation-duration: Transition duration in ms
     * - data-enable-back-in-stock: "true" | "false" - Enable back in stock popup
     * - data-form-id: ID of the product form to submit to
     * - data-is-preorder: "true" | "false" - Is this a pre-order product
     * - data-variant-inventories: JSON with inventory data per variant
     */
    class AgpStickyAtc extends HTMLElement {
        constructor() {
            super();
            this.isVisible = false;
            this.isLoading = false;
            this.formElement = null;
            this.latestFormCondition = false;
            this.latestScrollCondition = true;
            this.variants = [];
            this.currentVariant = null;
            this.variantInventories = {};
            this.variantPrices = {};
            this.isPreorder = false;
            this.cartQuantities = {};
            this.notificationClasses = [];
            this.variantSelects = [];
            this.buttons = [];
            
            // Bind methods
            this.onIntersection = this.onIntersection.bind(this);
            this.onVariantChange = this.onVariantChange.bind(this);
            this.onButtonClick = this.onButtonClick.bind(this);
            this.onMainFormVariantChange = this.onMainFormVariantChange.bind(this);
            this.onCartUpdate = this.onCartUpdate.bind(this);
            this.onScroll = this.onScroll.bind(this);
        }
        
        connectedCallback() {
            // Move to body to escape any parent stacking contexts
            // This ensures the fixed positioning works relative to viewport
            if (this.parentElement !== document.body) {
                document.body.appendChild(this);
                return; // connectedCallback will be called again once appended to body
            }
            
            // Parse preorder and inventory data
            this.isPreorder = this.dataset.isPreorder === 'true';
            if (this.dataset.variantInventories) {
                try {
                    this.variantInventories = JSON.parse(this.dataset.variantInventories);
                } catch (e) {
                    console.error('AGP Sticky ATC: Failed to parse variant inventories', e);
                }
            }
            
            // Parse Liquid-rendered variant prices (locale-correct, no Intl.NumberFormat needed)
            if (this.dataset.variantPrices) {
                try {
                    this.variantPrices = JSON.parse(this.dataset.variantPrices);
                } catch (e) {
                    console.error('AGP Sticky ATC: Failed to parse variant prices', e);
                }
            }
            
            // Parse notification custom classes
            if (this.dataset.notificationClasses) {
                this.notificationClasses = this.dataset.notificationClasses.split(' ').filter(cls => cls.trim());
            }
            
            // Get DOM elements
            this.formId = this.dataset.formId;
            this.formElement = document.getElementById(this.formId);
            // Support multiple buttons (one per layout - full and compact)
            this.buttons = this.querySelectorAll('[data-sticky-atc-button]');
            // Support multiple variant selects (one per layout) with agp-select wrapper
            // The data-sticky-variant-select attribute is on the select element itself
            this.variantSelects = [];
            this.querySelectorAll('[data-sticky-variant-select]').forEach(el => {
                // Check if the element itself is a select or if it's a wrapper containing one
                if (el.tagName === 'SELECT') {
                    this.variantSelects.push(el);
                } else {
                    const select = el.querySelector('select, .agp-select__native');
                    if (select) this.variantSelects.push(select);
                }
            });
            this.priceElements = this.querySelectorAll('[data-sticky-price]');
            this.buttonTextElements = this.querySelectorAll('[data-button-text]');
            this.imageElement = this.querySelector('[data-sticky-image] img');
            this.variantNameElement = this.querySelector('[data-sticky-variant-name]');
            this.isFullLayout = this.dataset.layout === 'full';
            
            // Parse variants JSON
            const variantsJson = document.querySelector('[data-sticky-atc-variants]');
            if (variantsJson) {
                try {
                    this.variants = JSON.parse(variantsJson.textContent);
                    // Set initial variant from first select or from main form
                    const firstSelect = this.variantSelects[0];
                    const selectedOption = firstSelect?.querySelector('option:checked');
                    if (selectedOption) {
                        this.currentVariant = this.variants.find(v => v.id === parseInt(selectedOption.value));
                    } else {
                        // For full layout or when no select, get from main form or URL
                        const mainVariantInput = this.formElement?.querySelector('input[name="id"]');
                        if (mainVariantInput) {
                            this.currentVariant = this.variants.find(v => v.id === parseInt(mainVariantInput.value));
                        } else if (this.variants.length > 0) {
                            // Fallback to first available or first variant
                            this.currentVariant = this.variants.find(v => v.available) || this.variants[0];
                        }
                    }
                } catch (e) {
                    console.error('AGP Sticky ATC: Failed to parse variants', e);
                }
            }
            
            // Check for debug mode (always visible)
            this.isDebugMode = this.dataset.debug === 'true';
            
            if (this.isDebugMode) {
                // In debug mode, always show the sticky bar
                this.isVisible = true;
                this.classList.add('is-visible');
            } else {
                // Setup IntersectionObserver for normal behavior
                this.observer = new IntersectionObserver(this.onIntersection, {
                    threshold: 0
                });
                
                // Find the buy buttons element to observe
                const buyButtons = this.formElement?.querySelector('buy-buttons, .buy-buttons, [data-buy-buttons]');
                if (buyButtons) {
                    this.buyButtonsElement = buyButtons;
                    this.observer.observe(buyButtons);
                } else if (this.formElement) {
                    // Fallback to form element
                    this.buyButtonsElement = this.formElement;
                    this.observer.observe(this.formElement);
                }
                
                // Observe footer
                window.addEventListener('scroll', this.onScroll, { passive: true });
            }
            
            // Event listeners for all variant selects
            this.variantSelects.forEach(select => {
                select.addEventListener('change', this.onVariantChange);
            });
            // Event listeners for all buttons
            this.buttons.forEach(button => {
                button.addEventListener('click', this.onButtonClick);
            });
            
            // Listen to main form variant changes on document (variant:change bubbles).
            // We listen on document instead of formElement because block rendering
            // order may cause formElement to not exist yet at connectedCallback time.
            document.addEventListener('variant:change', this.onMainFormVariantChange);
            
            // Poll the hidden variant input as fallback for themes that don't dispatch events.
            // Defer lookup if formElement isn't available yet (block ordering).
            this.setupVariantInputPoll();
            
            // Listen for cart updates to check max-in-cart
            // (cart:refresh/cart:add from our own components, cart:change from theme on item removal/quantity change)
            document.addEventListener('cart:refresh', this.onCartUpdate);
            document.addEventListener('cart:add', this.onCartUpdate);
            document.addEventListener('cart:change', this.onCartUpdate);
            
            // Fetch initial cart quantities
            this.fetchCartQuantities();
        }
        
        disconnectedCallback() {
            this.observer?.disconnect();
            window.removeEventListener('scroll', this.onScroll);
            if (this.variantInputPoll) {
                clearInterval(this.variantInputPoll);
            }
            this.variantSelects.forEach(select => {
                select.removeEventListener('change', this.onVariantChange);
            });
            this.buttons.forEach(button => {
                button.removeEventListener('click', this.onButtonClick);
            });
            this.formElement?.removeEventListener('variant:change', this.onMainFormVariantChange);
            document.removeEventListener('variant:change', this.onMainFormVariantChange);
            document.removeEventListener('cart:refresh', this.onCartUpdate);
            document.removeEventListener('cart:add', this.onCartUpdate);
            document.removeEventListener('cart:change', this.onCartUpdate);
        }
        
        async fetchCartQuantities() {
            try {
                const response = await fetch('/cart.js');
                const cart = await response.json();
                this.cartQuantities = {};
                cart.items.forEach(item => {
                    this.cartQuantities[item.variant_id] = (this.cartQuantities[item.variant_id] || 0) + item.quantity;
                });
                this.updateUI();
            } catch (e) {
                console.error('AGP Sticky ATC: Failed to fetch cart', e);
            }
        }
        
        onCartUpdate() {
            this.fetchCartQuantities();
        }
        
        /**
         * Set up variant input polling with deferred form lookup.
         * If the form element doesn't exist yet (block ordering), retries after DOM is ready.
         */
        setupVariantInputPoll() {
            const trySetup = () => {
                if (!this.formElement) {
                    this.formElement = document.getElementById(this.formId);
                }
                if (!this.formElement) return false;
                
                // Set up observer if not done yet (form wasn't available at connectedCallback time)
                if (!this.buyButtonsElement && !this.isDebugMode) {
                    const buyButtons = this.formElement.querySelector('buy-buttons, .buy-buttons, [data-buy-buttons]');
                    if (buyButtons) {
                        this.buyButtonsElement = buyButtons;
                        this.observer?.observe(buyButtons);
                    } else {
                        this.buyButtonsElement = this.formElement;
                        this.observer?.observe(this.formElement);
                    }
                }
                
                // Set up initial variant from form if not yet determined
                if (!this.currentVariant || this.currentVariant === this.variants[0]) {
                    const mainVariantInput = this.formElement.querySelector('input[name="id"]');
                    if (mainVariantInput) {
                        const formVariant = this.variants.find(v => v.id === parseInt(mainVariantInput.value));
                        if (formVariant) this.currentVariant = formVariant;
                    }
                }
                
                // Set up polling
                this.variantInput = this.formElement.querySelector('input[name="id"]');
                if (this.variantInput) {
                    this.lastVariantInputValue = this.variantInput.value;
                    this.variantInputPoll = setInterval(() => {
                        if (this.variantInput.value !== this.lastVariantInputValue) {
                            this.lastVariantInputValue = this.variantInput.value;
                            const newVariantId = parseInt(this.variantInput.value);
                            if (this.currentVariant?.id !== newVariantId) {
                                this.currentVariant = this.variants.find(v => v.id === newVariantId);
                                this.variantSelects.forEach(select => {
                                    if (select.value !== String(newVariantId)) {
                                        select.value = newVariantId;
                                    }
                                });
                                this.updateUI();
                            }
                        }
                    }, 100);
                }
                return true;
            };
            
            // Try immediately, then defer if form not found
            if (!trySetup()) {
                requestAnimationFrame(() => {
                    if (!trySetup()) {
                        // Last resort: try after DOM is fully loaded
                        if (document.readyState === 'loading') {
                            document.addEventListener('DOMContentLoaded', () => trySetup(), { once: true });
                        } else {
                            setTimeout(() => trySetup(), 100);
                        }
                    }
                });
            }
        }
        
        onIntersection(entries) {
            entries.forEach(entry => {
                if (entry.target === this.buyButtonsElement || entry.target === this.formElement) {
                    if (!entry.isIntersecting) {
                        const isAboveViewport = entry.boundingClientRect.bottom < 0;
                        const isBelowViewport = entry.boundingClientRect.top > window.innerHeight;
                        
                        if (isAboveViewport) {
                            // Button scrolled past (normal behavior)
                            this.latestFormCondition = true;
                        } else if (isBelowViewport) {
                            // Button is below viewport — check per-device setting
                            const isMobile = window.innerWidth < 1000;
                            const showAbove = isMobile 
                                ? this.dataset.showAboveButtonMobile === 'true'
                                : this.dataset.showAboveButtonDesktop === 'true';
                            this.latestFormCondition = showAbove;
                        } else {
                            this.latestFormCondition = false;
                        }
                    } else {
                        this.latestFormCondition = false;
                    }
                }
            });
            
            this.updateVisibility();
        }
        
        onScroll() {
            // Hide when within the last 200px of the page
            const distanceFromBottom = document.documentElement.scrollHeight - window.scrollY - window.innerHeight;
            const newCondition = distanceFromBottom > 200;
            if (newCondition !== this.latestScrollCondition) {
                this.latestScrollCondition = newCondition;
                this.updateVisibility();
            }
        }
        
        updateVisibility() {
            const shouldShow = this.latestFormCondition && this.latestScrollCondition;
            
            if (shouldShow !== this.isVisible) {
                this.isVisible = shouldShow;
                this.classList.toggle('is-visible', shouldShow);
            }
        }
        
        onVariantChange(event) {
            const selectedId = parseInt(event.target.value);
            this.currentVariant = this.variants.find(v => v.id === selectedId);
            
            // Sync all sticky ATC variant selects
            this.variantSelects.forEach(select => {
                if (select !== event.target && select.value !== String(selectedId)) {
                    select.value = selectedId;
                }
            });
            
            // Try to use the theme's variant-picker element's selectVariant method
            // Find the variant-picker linked to our form
            let variantPicker = document.querySelector(`variant-picker[form="${this.formId}"]`);
            
            // If not found with form attribute, try to find any variant-picker on the page
            if (!variantPicker) {
                variantPicker = document.querySelector('variant-picker');
            }
            
            if (variantPicker && typeof variantPicker.selectVariant === 'function') {
                variantPicker.selectVariant(selectedId);
            } else {
                // Fallback: Update the master selector (input[name="id"]) directly
                const mainVariantInput = this.formElement?.querySelector('input[name="id"]');
                if (mainVariantInput && mainVariantInput.value !== String(selectedId)) {
                    mainVariantInput.value = selectedId;
                    mainVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
                }
                
                // Also dispatch variant:change on the form for other components to listen
                this.formElement?.dispatchEvent(new CustomEvent('variant:change', {
                    bubbles: true,
                    detail: {
                        variant: this.currentVariant
                    }
                }));
            }
            
            // Update URL
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set('variant', selectedId);
            history.replaceState({}, '', newUrl.toString());
            
            this.updateUI();
        }
        
        onMainFormVariantChange(event) {
            // When main form variant changes (from theme's variant picker), sync sticky bar.
            // Filter by form ID since we listen on document (variant:change bubbles).
            const sourceForm = event.target.closest('form') || event.target;
            if (this.formId && sourceForm.id !== this.formId) {
                return;
            }
            
            const newVariant = event.detail?.variant;
            if (newVariant) {
                const newVariantId = newVariant.id;
                // Sync selects if they exist
                this.variantSelects.forEach(select => {
                    if (select.value !== String(newVariantId)) {
                        select.value = newVariantId;
                    }
                });
                this.currentVariant = this.variants.find(v => v.id === newVariantId);
                this.updateUI();
            }
        }
        
        updateUI() {
            if (!this.currentVariant) return;
            
            const variantId = this.currentVariant.id;
            const isAvailable = this.currentVariant.available;
            const enableBackInStock = this.dataset.enableBackInStock === 'true';
            
            // Check max-in-cart state (use String key for JSON lookup)
            const inventoryData = this.variantInventories[String(variantId)];
            const cartQty = this.cartQuantities[variantId] || 0;
            let isMaxInCart = false;
            
            if (inventoryData && isAvailable) {
                // Only check max if inventory policy is 'deny' (don't allow overselling)
                if (inventoryData.inventory_policy === 'deny') {
                    const maxQty = inventoryData.inventory_quantity;
                    if (cartQty >= maxQty && maxQty > 0) {
                        isMaxInCart = true;
                    }
                }
            }
            
            // Get formatted price — prefer Liquid-rendered prices (locale-correct)
            // over Intl.NumberFormat which varies by browser locale
            let formattedPrice = null;
            const variantPriceFromMap = this.variantPrices?.[String(this.currentVariant.id)];
            if (variantPriceFromMap) {
                formattedPrice = variantPriceFromMap;
            } else {
                // Fallback: try select option data-price attribute
                const firstSelect = this.variantSelects[0];
                if (firstSelect) {
                    const selectedOption = firstSelect.querySelector(`option[value="${this.currentVariant.id}"]`);
                    if (selectedOption) {
                        formattedPrice = selectedOption.dataset.price;
                    }
                }
            }
            
            // Update all price elements
            this.priceElements.forEach(el => {
                el.textContent = formattedPrice || this.formatMoney(this.currentVariant.price);
            });
            
            // Update variant name (full layout only)
            if (this.variantNameElement && this.currentVariant.title) {
                this.variantNameElement.textContent = this.currentVariant.title;
            }
            
            // Update image if variant has featured_image
            if (this.imageElement && this.currentVariant.featured_image) {
                const newSrc = this.currentVariant.featured_image.src.replace(/(\.[^.]+)$/, '_200x200$1');
                this.imageElement.src = newSrc;
                this.imageElement.alt = this.currentVariant.featured_image.alt || '';
            }
            
            // Helper to convert hex to rgb
            const hexToRgb = (hex) => {
                const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : null;
            };
            
            // Update all buttons
            this.buttons.forEach(button => {
                // Reset custom disabled colors first
                button.style.removeProperty('--agp-btn-disabled-bg');
                button.style.removeProperty('--agp-btn-disabled-text');
                button.classList.remove('is-max-in-cart');
                
                // Remove notification custom classes if previously added
                if (this.notificationClasses) {
                    this.notificationClasses.forEach(cls => button.classList.remove(cls));
                }
                
                if (isMaxInCart) {
                    // Max in cart state
                    button.disabled = true;
                    button.classList.add('is-disabled');
                    button.classList.add('is-max-in-cart');
                    
                    // Apply custom disabled colors if set
                    const disabledBg = this.dataset.disabledBg;
                    const disabledText = this.dataset.disabledText;
                    if (disabledBg) {
                        const rgb = hexToRgb(disabledBg);
                        if (rgb) button.style.setProperty('--agp-btn-disabled-bg', rgb);
                    }
                    if (disabledText) {
                        const rgb = hexToRgb(disabledText);
                        if (rgb) button.style.setProperty('--agp-btn-disabled-text', rgb);
                    }
                } else if (isAvailable) {
                    button.disabled = false;
                    button.classList.remove('is-disabled');
                } else if (enableBackInStock) {
                    button.disabled = false;
                    button.classList.remove('is-disabled');
                    // Add notification custom classes
                    if (this.notificationClasses) {
                        this.notificationClasses.forEach(cls => button.classList.add(cls));
                    }
                } else {
                    button.disabled = true;
                    button.classList.add('is-disabled');
                }
            });
            
            // Update all button text elements
            this.buttonTextElements.forEach(textEl => {
                const button = textEl.closest('[data-sticky-atc-button]');
                if (button) {
                    if (isMaxInCart) {
                        textEl.textContent = button.dataset.maxInCartText || 'Max quantity reached';
                    } else if (isAvailable) {
                        // Use preorder text if this is a preorder product
                        if (this.isPreorder && button.dataset.preorderText) {
                            textEl.textContent = button.dataset.preorderText;
                        } else {
                            textEl.textContent = button.dataset.addText;
                        }
                    } else if (enableBackInStock) {
                        textEl.textContent = button.dataset.backInStockText;
                    } else {
                        textEl.textContent = button.dataset.soldOutText;
                    }
                }
            });
            
            // Update price visibility based on state
            this.updatePriceVisibility(isMaxInCart, isAvailable, enableBackInStock);
        }
        
        updatePriceVisibility(isMaxInCart, isAvailable, enableBackInStock) {
            // Determine which price settings to use based on state
            let showPriceDesktop = false;
            let showPriceMobile = false;
            
            if (isMaxInCart) {
                showPriceDesktop = this.dataset.showPriceMaxQtyDesktop === 'true';
                showPriceMobile = this.dataset.showPriceMaxQtyMobile === 'true';
            } else if (isAvailable) {
                if (this.isPreorder) {
                    showPriceDesktop = this.dataset.showPricePreorderDesktop === 'true';
                    showPriceMobile = this.dataset.showPricePreorderMobile === 'true';
                } else {
                    showPriceDesktop = this.dataset.showPriceDesktop === 'true';
                    showPriceMobile = this.dataset.showPriceMobile === 'true';
                }
            } else if (enableBackInStock) {
                showPriceDesktop = this.dataset.showPriceNotificationDesktop === 'true';
                showPriceMobile = this.dataset.showPriceNotificationMobile === 'true';
            }
            // For sold out (disabled), we use false by default
            
            // Find all price wrappers and update visibility
            // Desktop layout has class agp-sm-hidden (hidden on mobile)
            // Mobile content has class agp-lg-hidden (hidden on desktop)
            this.querySelectorAll('[data-price-wrapper]').forEach(wrapper => {
                const button = wrapper.closest('[data-sticky-atc-button]');
                if (!button) return;
                
                // Check if this is in desktop or mobile content
                const isInDesktopContent = wrapper.closest('.agp-sm-hidden') !== null;
                const isInMobileContent = wrapper.closest('.agp-lg-hidden') !== null;
                
                let shouldShow = false;
                if (isInDesktopContent) {
                    shouldShow = showPriceDesktop;
                } else if (isInMobileContent) {
                    shouldShow = showPriceMobile;
                } else {
                    // Neither - likely in a layout that shows on both
                    // Use desktop setting as default
                    shouldShow = showPriceDesktop;
                }
                
                wrapper.style.display = shouldShow ? '' : 'none';
            });
        }
        
        handleClickEffect(button, event) {
            const clickEffect = button.dataset.clickEffect;
            
            if (!clickEffect || clickEffect === 'none') return;
            
            switch (clickEffect) {
                case 'ripple':
                    // Create ripple element at click position
                    const rect = button.getBoundingClientRect();
                    const x = event.clientX - rect.left;
                    const y = event.clientY - rect.top;
                    const size = Math.max(rect.width, rect.height) * 2;
                    
                    const ripple = document.createElement('span');
                    ripple.className = 'agp-btn__ripple';
                    ripple.style.width = ripple.style.height = `${size}px`;
                    ripple.style.left = `${x - size / 2}px`;
                    ripple.style.top = `${y - size / 2}px`;
                    
                    button.appendChild(ripple);
                    
                    // Remove ripple after animation
                    ripple.addEventListener('animationend', () => ripple.remove());
                    break;
                    
                case 'bounce':
                    // Add and remove class for bounce animation
                    button.classList.add('is-clicked');
                    button.addEventListener('animationend', () => {
                        button.classList.remove('is-clicked');
                    }, { once: true });
                    break;
                    
                // scale, press, flash are handled purely by CSS :active state
            }
        }
        
        async onButtonClick(event) {
            event.preventDefault();
            
            const button = event.currentTarget;
            
            // Handle click effects
            this.handleClickEffect(button, event);
            
            if (this.isLoading) return;
            
            const variantId = this.currentVariant?.id;
            const isAvailable = this.currentVariant?.available ?? true;
            const enableBackInStock = this.dataset.enableBackInStock === 'true';
            
            // Check max-in-cart state (use String key for JSON lookup)
            const inventoryData = this.variantInventories[String(variantId)];
            const cartQty = this.cartQuantities[variantId] || 0;
            let isMaxInCart = false;
            
            if (inventoryData && isAvailable) {
                if (inventoryData.inventory_policy === 'deny') {
                    const maxQty = inventoryData.inventory_quantity;
                    if (cartQty >= maxQty && maxQty > 0) {
                        isMaxInCart = true;
                    }
                }
            }
            
            // Block if max in cart reached
            if (isMaxInCart) return;
            
            // Handle back in stock
            if (!isAvailable && enableBackInStock) {
                this.openBackInStockPopup();
                return;
            }
            
            if (!isAvailable) return;
            
            // Add to cart
            this.isLoading = true;
            this.classList.add('is-loading');
            
            try {
                const resolvedVariantId = variantId || this.variantSelects[0]?.value || 
                    this.formElement?.querySelector('input[name="id"]')?.value;
                
                if (!resolvedVariantId) {
                    console.error('AGP Sticky ATC: No variant ID found');
                    this.isLoading = false;
                    this.classList.remove('is-loading');
                    return;
                }
                
                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({
                        items: [{
                            id: parseInt(resolvedVariantId),
                            quantity: 1
                        }]
                    })
                });
                
                if (!response.ok) {
                    throw new Error('Failed to add to cart');
                }
                
                const data = await response.json();
                
                // Dispatch event for cart drawer/notification
                document.dispatchEvent(new CustomEvent('cart:refresh'));
                
                // Try to open cart drawer if it exists
                const cartDrawer = document.querySelector('cart-drawer');
                if (cartDrawer && typeof cartDrawer.show === 'function') {
                    cartDrawer.show();
                } else {
                    // Trigger Shopify's native cart update event
                    document.dispatchEvent(new CustomEvent('cart:add', { detail: data }));
                }
                
            } catch (error) {
                console.error('AGP Sticky ATC: Error adding to cart', error);
            } finally {
                this.isLoading = false;
                this.classList.remove('is-loading');
            }
        }
        
        openBackInStockPopup() {
            // Try to find and open the theme's back in stock popup if it exists
            const backInStockPopup = document.querySelector('[data-back-in-stock-popup], .back-in-stock-popup, back-in-stock-popup');
            if (backInStockPopup) {
                if (typeof backInStockPopup.show === 'function') {
                    backInStockPopup.show();
                } else {
                    backInStockPopup.classList.add('is-visible');
                }
                return;
            }
            
            // Fallback: dispatch event for other components to handle
            document.dispatchEvent(new CustomEvent('back-in-stock:open', {
                detail: { variantId: this.currentVariant?.id }
            }));
        }
        
        formatMoney(cents) {
            const formatter = new Intl.NumberFormat(Shopify.locale || 'en', {
                style: 'currency',
                currency: Shopify.currency?.active || 'EUR'
            });
            let formatted = formatter.format(cents / 100);
            // Append currency code if theme setting enables it (matches Shopify's money_with_currency filter)
            if (this.dataset.currencyCodeEnabled === 'true') {
                const code = Shopify.currency?.active || 'EUR';
                if (!formatted.includes(code)) {
                    formatted += ' ' + code;
                }
            }
            return formatted;
        }
    }
    
    // Register sticky ATC custom element
    if (!customElements.get('agp-sticky-atc')) {
        customElements.define('agp-sticky-atc', AgpStickyAtc);
    }

    /**
     * Fix product card badges: ensure the sold-out badge is visible alongside
     * custom badges. The theme's product-badges.liquid hides the sold-out badge
     * when a coming_soon metafield is present, but we want both to show.
     * Appends the sold-out badge after all other badges so it appears below.
     * 
     * Uses a MutationObserver to also fix badges in dynamically loaded content
     * (facet filtering, search results, pagination, predictive search).
     */
    function fixBadgeList(badgeList) {
        const soldOutBadge = badgeList.querySelector('sold-out-badge[hidden]');
        if (!soldOutBadge) return;

        const hasCustomBadge = badgeList.querySelector('.badge--custom, .badge--coming-soon');
        if (!hasCustomBadge) return;

        // Move sold-out badge to end (renders below custom badges in vertical layout)
        badgeList.appendChild(soldOutBadge);
        soldOutBadge.removeAttribute('hidden');
        badgeList.removeAttribute('hidden');
    }

    function initProductCardBadges() {
        document.querySelectorAll('badge-list').forEach(fixBadgeList);

        // Observe DOM for dynamically inserted badge-lists (facet updates, search, pagination)
        new MutationObserver(mutations => {
            for (const mutation of mutations) {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType !== Node.ELEMENT_NODE) continue;
                    if (node.matches?.('badge-list')) {
                        fixBadgeList(node);
                    } else {
                        node.querySelectorAll?.('badge-list').forEach(fixBadgeList);
                    }
                }
            }
        }).observe(document.body, { childList: true, subtree: true });
    }

    /**
     * =========================================================================
     * AGP Card Carousel — Shared Module-Level Resources
     * =========================================================================
     * Performance: collections can render hundreds of carousels via infinite
     * scroll. Per-instance document listeners, matchMedia listeners and
     * IntersectionObservers add up quickly. These shared singletons replace
     * per-instance handlers and dispatch to a registry of registered
     * carousels instead.
     */
    // Registry of carousels with [data-reset-others] — used by the single
    // document-level coordinator that resets siblings when one is navigated.
    const __agpResetOthersRegistry = new Set();
    // Registry of all live carousels — subscribed to the shared matchMedia
    // listener for breakpoint changes.
    const __agpBpSubscribers = new Set();
    const __agpDesktopMql = window.matchMedia('(min-width: 1000px)');
    __agpDesktopMql.addEventListener('change', () => {
        __agpBpSubscribers.forEach((c) => {
            try { c.onBreakpointChange(); } catch (e) { /* noop */ }
        });
    });
    // Shared IntersectionObserver for the carousel "hint" peek animation.
    // Each carousel registers itself; the observer dispatches tryHint() on
    // the matching instance when the threshold is reached.
    const __agpHintMap = new WeakMap();
    const __agpHintObserver = ('IntersectionObserver' in window)
        ? new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.9) {
                    const c = __agpHintMap.get(entry.target);
                    if (c) c.tryHint();
                }
            });
        }, { threshold: 0.9 })
        : null;

    // Shared IntersectionObserver that *activates* carousels only once they
    // come close to the viewport. Long product lists (200+ items) instantiate
    // many <agp-card-carousel> elements; doing the full init (event listeners,
    // image preloads, dot setup, hint registration) eagerly on every one of
    // them is wasteful and noticeable on slower devices. The activate
    // observer uses a generous rootMargin so visible carousels and their
    // immediate neighbours are ready before the user can scroll to them, but
    // far-off carousels stay completely dormant.
    const __agpActivateObserver = ('IntersectionObserver' in window)
        ? new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const el = entry.target;
                    __agpActivateObserver.unobserve(el);
                    if (typeof el._activate === 'function') el._activate();
                }
            });
        }, { rootMargin: '1500px 0px' })
        : null;

    /**
     * =========================================================================
     * AGP Card Carousel Custom Element
     * =========================================================================
     * Horizontal image carousel for AGP product cards.
     *
     * Features:
     * - Touch swipe on mobile, chevron arrows on desktop hover
     * - Transform-based sliding (no scroll-snap, full animation control)
     * - Lazy preload: loads next image when user swipes to adjacent slide
     * - "Hint" animation: 20% peek of second image on first view
     * - Respects display:none slides (metafield visibility per device)
     * - Recalculates on breakpoint change (matchMedia listener)
     *
     * Attributes:
     * - data-hint-desktop: Enable hint animation on desktop
     * - data-hint-mobile: Enable hint animation on mobile
     */
    if (!customElements.get('agp-card-carousel')) {
        customElements.define('agp-card-carousel', class extends HTMLElement {
            connectedCallback() {
                this.track = this.querySelector('.agp-card-carousel__track');
                if (!this.track) return;

                // Defer the (relatively expensive) full init until the carousel
                // is within the activate observer's rootMargin. For long
                // grids this means most carousels do essentially no work on
                // page load. If the observer isn't available, activate now.
                if (__agpActivateObserver) {
                    __agpActivateObserver.observe(this);
                } else {
                    this._activate();
                }
            }

            _activate() {
                if (this._activated) return;
                this._activated = true;

                this.allSlides = [...this.track.children];
                this.originalSlides = [...this.allSlides]; // preserve original DOM order
                this.index = 0;
                this.hinted = false;
                this.wasSwiping = false;
                this.loop = this.hasAttribute('data-loop');
                this.videoPauseOffscreen = this.hasAttribute('data-video-pause-offscreen');
                this.resetOthers = this.hasAttribute('data-reset-others');

                // Arrow navigation
                const prevBtn = this.querySelector('.agp-card-carousel__arrow--prev');
                const nextBtn = this.querySelector('.agp-card-carousel__arrow--next');
                if (prevBtn) prevBtn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.userNav('prev'); });
                if (nextBtn) nextBtn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.userNav('next'); });

                // Touch swipe
                this.track.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: true });
                this.track.addEventListener('touchmove', (e) => this.onTouchMove(e), { passive: false });
                this.track.addEventListener('touchend', (e) => this.onTouchEnd(e), { passive: true });

                // Mouse drag swipe (desktop)
                this.track.addEventListener('mousedown', (e) => this.onMouseDown(e));
                this._onMouseMove = (e) => this.onMouseMove(e);
                this._onMouseUp = (e) => this.onMouseUp(e);

                // Prevent native image drag inside the carousel
                this.track.addEventListener('dragstart', (e) => e.preventDefault());

                // Prevent click navigation when swiping
                this.track.addEventListener('click', (e) => {
                    if (this.wasSwiping) {
                        e.preventDefault();
                        this.wasSwiping = false;
                    }
                }, true);

                // Breakpoint change: recalculate when metafield slides show/hide.
                // Uses a single shared matchMedia listener (see module top).
                __agpBpSubscribers.add(this);

                // Reset-others: register in shared coordinator instead of
                // adding a per-instance document listener.
                if (this.resetOthers) {
                    __agpResetOthersRegistry.add(this);
                }

                // Preload initial slide (needed for deferred videos at index 0)
                this.preloadSlide(this.index);
                // Preload adjacent slides so neighbour image/video is ready on
                // first swipe in either direction. For looped carousels, the
                // previous neighbour wraps to the last slide.
                this.preloadSlide(this.index + 1);
                if (this.index > 0) {
                    this.preloadSlide(this.index - 1);
                } else if (this.loop) {
                    const slides = this.visibleSlides;
                    if (slides.length > 1) this.preloadSlide(slides.length - 1);
                }

                // Set initial arrow state
                this.updateArrows();

                // Dots navigation (mobile-only, rendered as sibling after the
                // carousel inside .product-card__figure). Buttons follow the
                // same slide order; visibility classes on the buttons must
                // mirror the slide visibility classes so visibleDots maps 1:1
                // to visibleSlides.
                this.setupDots();

                // Play button on non-autoplay videos (click plays video, doesn't navigate)
                this.querySelectorAll('.agp-card-carousel__play').forEach(btn => {
                    btn.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const slide = btn.closest('.agp-card-carousel__slide--video');
                        const video = slide && slide.querySelector('video');
                        if (video) {
                            this.preloadSlide(this.visibleSlides.indexOf(slide));
                            video.play().catch(() => {});
                            btn.style.display = 'none';
                        }
                    });
                });

                // Hint animation via IntersectionObserver
                this.setupHint();
            }

            disconnectedCallback() {
                if (__agpActivateObserver) __agpActivateObserver.unobserve(this);
                __agpBpSubscribers.delete(this);
                __agpResetOthersRegistry.delete(this);
                if (__agpHintObserver) {
                    __agpHintObserver.unobserve(this);
                    __agpHintMap.delete(this);
                }
            }

            /** Invalidate cached visibleSlides/visibleDots (call after order or display changes) */
            _invalidateVisibleCache() {
                this._visibleSlidesCache = null;
                this._visibleDotsCache = null;
            }

            /** Get only visible (not display:none) slides — cached */
            get visibleSlides() {
                if (!this._visibleSlidesCache) {
                    this._visibleSlidesCache = this.allSlides.filter(s => getComputedStyle(s).display !== 'none');
                }
                return this._visibleSlidesCache;
            }

            /* ----- Navigation ----- */

            /** Navigate to a slide by visible index */
            goTo(visibleIndex) {
                const slides = this.visibleSlides;
                if (visibleIndex < 0 || visibleIndex >= slides.length) return;

                // Pause video on the slide we're leaving
                if (this.videoPauseOffscreen && visibleIndex !== this.index) {
                    const leaving = slides[this.index];
                    if (leaving) {
                        const vid = leaving.querySelector('video');
                        if (vid && !vid.paused) vid.pause();
                    }
                }

                this.index = visibleIndex;
                const offset = slides[visibleIndex].offsetLeft;
                this.track.style.transform = `translateX(-${offset}px)`;
                this.preloadSlide(visibleIndex + 1);
                this.preloadSlide(visibleIndex - 1);

                // When looping, also preload the wrap-around slide so it's ready
                // for the next swipe at the boundary (avoids loading during drag)
                if (this.loop) {
                    if (visibleIndex === 0) this.preloadSlide(slides.length - 1);
                    if (visibleIndex === slides.length - 1) this.preloadSlide(0);
                }

                this.updateArrows();

                // Resume video on the slide we're arriving at
                if (this.videoPauseOffscreen) {
                    const arriving = slides[visibleIndex];
                    if (arriving) {
                        const vid = arriving.querySelector('video');
                        if (vid && vid.paused && vid.dataset.loaded) {
                            vid.play().catch(() => {});
                        }
                    }
                }
                this.updateDots();
            }

            /* ----- Dots ----- */

            /** Find dots container (sibling after the carousel, inside figure). */
            setupDots() {
                const parent = this.parentElement;
                this.dots = parent ? parent.querySelector('.agp-card-carousel__dots') : null;
                if (!this.dots) return;
                this.allDots = [...this.dots.querySelectorAll('button')];
                this.allDots.forEach((btn) => {
                    btn.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const idx = this.visibleDots.indexOf(btn);
                        if (idx >= 0) this.goTo(idx);
                    });
                });
                this.updateDots();
            }

            /** Visible dots (filter out display:none, matches visibleSlides). Cached. */
            get visibleDots() {
                if (!this.allDots) return [];
                if (!this._visibleDotsCache) {
                    this._visibleDotsCache = this.allDots.filter((b) => getComputedStyle(b).display !== 'none');
                }
                return this._visibleDotsCache;
            }

            /** Sync aria-current on dot buttons with current slide index. */
            updateDots() {
                if (!this.allDots) return;
                this.visibleDots.forEach((btn, i) => {
                    btn.setAttribute('aria-current', i === this.index ? 'true' : 'false');
                });
            }

            prev() {
                const slides = this.visibleSlides;
                if (this.index > 0) {
                    this.goTo(this.index - 1);
                } else if (this.loop && slides.length > 1) {
                    this.rotatePrev();
                }
            }

            next() {
                const slides = this.visibleSlides;
                if (this.index < slides.length - 1) {
                    this.goTo(this.index + 1);
                } else if (this.loop && slides.length > 1) {
                    this.rotateNext();
                }
            }

            /* ----- Infinite loop via DOM reordering ----- */

            /**
             * Restore slides to their original DOM order and reset index.
             * Used before breakpoint recalc and fadeReset to undo any rotation.
             */
            restoreOrder() {
                const currentSlide = this.visibleSlides[this.index];
                this.originalSlides.forEach(s => this.track.append(s));
                this.allSlides = [...this.track.children];
                this._invalidateVisibleCache();
                // Find where the current slide ended up after restore
                const slides = this.visibleSlides;
                const newIdx = slides.indexOf(currentSlide);
                this.index = newIdx >= 0 ? newIdx : 0;
            }

            /**
             * Move the last visible slide to the front of the track,
             * keep current slide visually stationary, then animate to the moved slide.
             */
            rotatePrev() {
                const slides = this.visibleSlides;
                const lastSlide = slides[slides.length - 1];

                // Preload the slide we're about to show
                this.preloadSlide(slides.length - 1);

                // Move last slide to the front of the DOM
                this.track.prepend(lastSlide);
                this.allSlides = [...this.track.children];
                this._invalidateVisibleCache();

                // Current slide shifted from index 0 to index 1 — reposition instantly
                this.index = 1;
                this.track.style.transition = 'none';
                this.track.style.transform = `translateX(-${this.visibleSlides[1].offsetLeft}px)`;
                this.track.offsetHeight; // reflow
                this.track.style.transition = '';

                // Animate to index 0 (the moved slide)
                this.goTo(0);
            }

            /**
             * Move the first visible slide to the end of the track,
             * keep current slide visually stationary, then animate to the moved slide.
             */
            rotateNext() {
                const slides = this.visibleSlides;
                const firstSlide = slides[0];

                // Preload the slide we're about to show
                this.preloadSlide(0);

                // Move first slide to the end of the DOM
                this.track.append(firstSlide);
                this.allSlides = [...this.track.children];
                this._invalidateVisibleCache();

                // Current slide shifted from last to second-to-last — reposition instantly
                const newSlides = this.visibleSlides;
                this.index = newSlides.length - 2;
                this.track.style.transition = 'none';
                this.track.style.transform = `translateX(-${newSlides[this.index].offsetLeft}px)`;
                this.track.offsetHeight; // reflow
                this.track.style.transition = '';

                // Animate to last index (the moved slide)
                this.goTo(newSlides.length - 1);
            }

            /** User-initiated navigation (arrows / swipe). Triggers reset on others. */
            userNav(dir) {
                this.cancelHint();
                if (dir === 'prev') this.prev();
                else this.next();
                if (this.resetOthers && __agpResetOthersRegistry.size > 1) {
                    // Direct iteration of the registry replaces a document-
                    // wide CustomEvent broadcast. Skip carousels that are far
                    // off-screen to avoid touching DOM for elements the user
                    // can't see.
                    const vh = window.innerHeight;
                    __agpResetOthersRegistry.forEach((c) => {
                        if (c === this) return;
                        const r = c.getBoundingClientRect();
                        if (r.bottom < -200 || r.top > vh + 200) return;
                        c.fadeReset();
                    });
                }
            }

            /** Soft reset: restore order, overlay first image, reposition, remove overlay */
            fadeReset() {
                // Restore original order so slides[0] is the real first slide
                this.restoreOrder();

                if (this.index === 0) return;

                // Pause any playing video on the current slide
                if (this.videoPauseOffscreen) {
                    const slides = this.visibleSlides;
                    const current = slides[this.index];
                    if (current) {
                        const vid = current.querySelector('video');
                        if (vid && !vid.paused) vid.pause();
                    }
                }

                // Reposition track to current slide (order just changed)
                const slides = this.visibleSlides;
                this.track.style.transition = 'none';
                this.track.style.transform = `translateX(-${slides[this.index].offsetLeft}px)`;
                this.track.offsetHeight;
                this.track.style.transition = '';

                const firstSlide = slides[0];
                if (!firstSlide) return;

                // Create overlay from first slide's image/poster
                const img = firstSlide.querySelector('img');
                const video = firstSlide.querySelector('video');
                const overlay = document.createElement('div');
                Object.assign(overlay.style, {
                    position: 'absolute', inset: '0', zIndex: '3',
                    opacity: '0', transition: 'opacity 0.3s ease',
                    pointerEvents: 'none'
                });

                if (img) {
                    const clone = img.cloneNode(true);
                    clone.style.width = '100%';
                    clone.style.height = '100%';
                    clone.style.objectFit = 'cover';
                    overlay.appendChild(clone);
                } else if (video) {
                    const poster = document.createElement('img');
                    poster.src = video.getAttribute('poster') || '';
                    Object.assign(poster.style, { width: '100%', height: '100%', objectFit: 'cover' });
                    overlay.appendChild(poster);
                }

                this.appendChild(overlay);
                overlay.offsetHeight; // reflow

                // Fade overlay in
                overlay.style.opacity = '1';

                const afterFadeIn = () => {
                    overlay.removeEventListener('transitionend', afterFadeIn);

                    // Jump track to first slide (hidden behind overlay)
                    this.index = 0;
                    const offset = slides[0] ? slides[0].offsetLeft : 0;
                    this.track.style.transition = 'none';
                    this.track.style.transform = `translateX(-${offset}px)`;
                    this.track.offsetHeight; // reflow
                    this.track.style.transition = '';
                    this.updateArrows();

                    // Fade overlay out to reveal real first slide
                    overlay.style.transition = 'opacity 0.3s ease';
                    overlay.style.opacity = '0';

                    const afterFadeOut = () => {
                        overlay.removeEventListener('transitionend', afterFadeOut);
                        overlay.remove();
                    };
                    overlay.addEventListener('transitionend', afterFadeOut);
                };
                overlay.addEventListener('transitionend', afterFadeIn);
            }

            /** Show/hide arrows based on boundary when loop is disabled */
            updateArrows() {
                if (this.loop) {
                    this.removeAttribute('data-at-start');
                    this.removeAttribute('data-at-end');
                    return;
                }
                const slides = this.visibleSlides;
                if (this.index <= 0) this.setAttribute('data-at-start', '');
                else this.removeAttribute('data-at-start');
                if (this.index >= slides.length - 1) this.setAttribute('data-at-end', '');
                else this.removeAttribute('data-at-end');
            }

            /** Load deferred images/videos for a visible slide index */
            preloadSlide(visibleIndex) {
                const slides = this.visibleSlides;
                if (visibleIndex < 0 || visibleIndex >= slides.length) return;
                const slide = slides[visibleIndex];

                // Handle deferred images (data-src)
                const img = slide.querySelector('img[data-src]');
                if (img) {
                    img.src = img.dataset.src;
                    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
                    if (img.dataset.sizes) img.sizes = img.dataset.sizes;
                    delete img.dataset.src;
                    delete img.dataset.srcset;
                    delete img.dataset.sizes;
                }

                // Force lazy images to load eagerly (carousel transforms move them
                // off-screen, preventing browser lazy loading from triggering)
                const lazyImg = slide.querySelector('img[loading="lazy"]');
                if (lazyImg) {
                    lazyImg.loading = 'eager';
                }

                // Handle deferred videos
                const video = slide.querySelector('video');
                if (video && !video.dataset.loaded) {
                    video.querySelectorAll('source[data-src]').forEach(source => {
                        source.src = source.dataset.src;
                        delete source.dataset.src;
                    });
                    video.load();
                    video.dataset.loaded = 'true';
                    if (video.hasAttribute('autoplay')) {
                        video.play().catch(() => {});
                    }
                }
            }

            /* ----- Mouse drag handling (desktop) ----- */
            onMouseDown(e) {
                if (e.button !== 0) return; // left click only
                this.cancelHint();
                this.startX = e.clientX;
                this.startY = e.clientY;
                this.dragDelta = 0;
                this.isDragging = false;
                this.isScrolling = null;
                this.rotatedForDrag = false;
                this.mouseDown = true;
                this.track.classList.add('agp-card-carousel__track--dragging');
                document.addEventListener('mousemove', this._onMouseMove);
                document.addEventListener('mouseup', this._onMouseUp);
            }

            onMouseMove(e) {
                if (!this.mouseDown) return;
                const dx = e.clientX - this.startX;
                const dy = e.clientY - this.startY;

                if (this.isScrolling === null && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
                    this.isScrolling = Math.abs(dy) > Math.abs(dx);
                }
                if (this.isScrolling) return;

                e.preventDefault();
                this.isDragging = true;
                this.dragDelta = dx;

                if (this.loop && !this.rotatedForDrag) {
                    const slides = this.visibleSlides;
                    if (dx > 0 && this.index === 0) {
                        const lastSlide = slides[slides.length - 1];
                        this.preloadSlide(slides.length - 1);
                        this.track.prepend(lastSlide);
                        this.allSlides = [...this.track.children];
                        this._invalidateVisibleCache();
                        this.index = 1;
                        this.rotatedForDrag = true;
                    } else if (dx < 0 && this.index === slides.length - 1) {
                        const firstSlide = slides[0];
                        this.preloadSlide(0);
                        this.track.append(firstSlide);
                        this.allSlides = [...this.track.children];
                        this._invalidateVisibleCache();
                        const newSlides = this.visibleSlides;
                        this.index = newSlides.length - 2;
                        this.rotatedForDrag = true;
                    }
                }

                const slides = this.visibleSlides;
                const base = slides[this.index] ? -slides[this.index].offsetLeft : 0;
                this.track.style.transform = `translateX(${base + dx}px)`;
            }

            onMouseUp() {
                if (!this.mouseDown) return;
                document.removeEventListener('mousemove', this._onMouseMove);
                document.removeEventListener('mouseup', this._onMouseUp);
                this.mouseDown = false;
                this.track.classList.remove('agp-card-carousel__track--dragging');
                if (this.isDragging && Math.abs(this.dragDelta) > 40) {
                    this.wasSwiping = true;
                    const slides = this.visibleSlides;
                    const swipingPrev = this.dragDelta > 0;
                    const swipingNext = this.dragDelta < 0;
                    if (!this.loop && swipingPrev && this.index <= 0) {
                        this.goTo(this.index);
                    } else if (!this.loop && swipingNext && this.index >= slides.length - 1) {
                        this.goTo(this.index);
                    } else if (swipingPrev) {
                        this.userNav('prev');
                    } else {
                        this.userNav('next');
                    }
                } else {
                    this.goTo(this.index);
                }
                this.startX = null;
                this.isDragging = false;
                this.isScrolling = null;
            }

            /* ----- Touch handling ----- */
            onTouchStart(e) {
                this.cancelHint();
                this.startX = e.touches[0].clientX;
                this.startY = e.touches[0].clientY;
                this.dragDelta = 0;
                this.isDragging = false;
                this.isScrolling = null;
                this.rotatedForDrag = false;
                this.track.classList.add('agp-card-carousel__track--dragging');
            }

            onTouchMove(e) {
                if (!this.startX) return;
                const dx = e.touches[0].clientX - this.startX;
                const dy = e.touches[0].clientY - this.startY;

                if (this.isScrolling === null && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
                    this.isScrolling = Math.abs(dy) > Math.abs(dx);
                }
                // Wait until we've decided which axis dominates before doing
                // anything. Calling preventDefault() while the direction is
                // still unknown (isScrolling === null) blocks the page's
                // vertical scroll on iOS Safari for the rest of the gesture.
                if (this.isScrolling !== false) return;

                e.preventDefault();
                this.isDragging = true;
                this.dragDelta = dx;

                // Preemptive DOM rotation for loop: place a slide in the drag direction
                // so the user sees content instead of blank space during the drag
                if (this.loop && !this.rotatedForDrag) {
                    const slides = this.visibleSlides;
                    if (dx > 0 && this.index === 0) {
                        // Swiping right at first slide: move last to front
                        const lastSlide = slides[slides.length - 1];
                        this.preloadSlide(slides.length - 1);
                        this.track.prepend(lastSlide);
                        this.allSlides = [...this.track.children];
                        this._invalidateVisibleCache();
                        this.index = 1;
                        this.rotatedForDrag = true;
                    } else if (dx < 0 && this.index === slides.length - 1) {
                        // Swiping left at last slide: move first to end
                        const firstSlide = slides[0];
                        this.preloadSlide(0);
                        this.track.append(firstSlide);
                        this.allSlides = [...this.track.children];
                        this._invalidateVisibleCache();
                        const newSlides = this.visibleSlides;
                        this.index = newSlides.length - 2;
                        this.rotatedForDrag = true;
                    }
                }

                const slides = this.visibleSlides;
                const base = slides[this.index] ? -slides[this.index].offsetLeft : 0;
                this.track.style.transform = `translateX(${base + dx}px)`;
            }

            onTouchEnd() {
                this.track.classList.remove('agp-card-carousel__track--dragging');
                if (this.isDragging && Math.abs(this.dragDelta) > 40) {
                    this.wasSwiping = true;
                    const slides = this.visibleSlides;
                    const swipingPrev = this.dragDelta > 0;
                    const swipingNext = this.dragDelta < 0;
                    if (!this.loop && swipingPrev && this.index <= 0) {
                        this.goTo(this.index);
                    } else if (!this.loop && swipingNext && this.index >= slides.length - 1) {
                        this.goTo(this.index);
                    } else if (swipingPrev) {
                        this.userNav('prev');
                    } else {
                        this.userNav('next');
                    }
                } else {
                    this.goTo(this.index);
                }
                this.startX = null;
                this.isDragging = false;
                this.isScrolling = null;
            }

            /* ----- Breakpoint change ----- */
            onBreakpointChange() {
                // Display rules may have changed between desktop/mobile;
                // drop cached visibleSlides/visibleDots so they re-filter.
                this._invalidateVisibleCache();
                // Restore original DOM order before recalculating
                this.restoreOrder();
                const slides = this.visibleSlides;
                if (this.index >= slides.length) this.index = Math.max(0, slides.length - 1);
                this.track.style.transition = 'none';
                if (slides[this.index]) {
                    this.track.style.transform = `translateX(-${slides[this.index].offsetLeft}px)`;
                }
                this.track.offsetHeight;
                this.track.style.transition = '';
                this.updateArrows();
                this.updateDots();
            }

            /* ----- Hint animation ----- */
            setupHint() {
                const hintDesktop = this.hasAttribute('data-hint-desktop');
                const hintMobile = this.hasAttribute('data-hint-mobile');
                if (!hintDesktop && !hintMobile) return;

                this.hintDesktop = hintDesktop;
                this.hintMobile = hintMobile;

                // Register with the shared module-level IntersectionObserver
                // instead of allocating one per carousel.
                if (__agpHintObserver) {
                    __agpHintMap.set(this, this);
                    __agpHintObserver.observe(this);
                }
            }

            tryHint() {
                if (this.hinted) return;

                const isDesktop = window.matchMedia('(min-width: 1000px)').matches;
                if (isDesktop && !this.hintDesktop) return;
                if (!isDesktop && !this.hintMobile) return;

                const slides = this.visibleSlides;
                if (slides.length < 2) return;

                // Check current and next slide images are loaded
                const imgs = slides.slice(this.index, this.index + 2)
                    .map(s => s.querySelector('img'))
                    .filter(Boolean);

                const allLoaded = imgs.every(img => img.complete && img.naturalWidth > 0);
                if (allLoaded) {
                    this.performHint();
                } else {
                    let loadCount = 0;
                    const check = () => {
                        loadCount++;
                        if (imgs.every(img => img.complete && img.naturalWidth > 0)) {
                            this.performHint();
                        }
                    };
                    imgs.forEach(img => {
                        if (!img.complete) img.addEventListener('load', check, { once: true });
                    });
                }
            }

            performHint() {
                if (this.hinted) return;
                this.hinted = true;
                if (__agpHintObserver) {
                    __agpHintObserver.unobserve(this);
                    __agpHintMap.delete(this);
                }

                const delay = parseInt(this.getAttribute('data-hint-delay')) || 0;

                this._hintTimer = setTimeout(() => {
                    this._hintTimer = null;
                    this.preloadSlide(this.index + 1);

                    const slides = this.visibleSlides;
                    const currentOffset = slides[this.index] ? slides[this.index].offsetLeft : 0;
                    const peekAmount = this.track.clientWidth * 0.2;

                    this.track.style.transition = 'transform 0.4s ease';
                    this.track.style.transform = `translateX(-${currentOffset + peekAmount}px)`;

                    setTimeout(() => {
                        this.track.style.transform = `translateX(-${currentOffset}px)`;
                        setTimeout(() => {
                            this.track.style.transition = '';
                        }, 400);
                    }, 500);
                }, delay);
            }

            /** Cancel any pending or in-progress hint animation */
            cancelHint() {
                this.hinted = true;
                if (__agpHintObserver) {
                    __agpHintObserver.unobserve(this);
                    __agpHintMap.delete(this);
                }
                if (this._hintTimer) {
                    clearTimeout(this._hintTimer);
                    this._hintTimer = null;
                }
                // performHint() sets an inline `transition: transform 0.4s ease`
                // to drive the peek animation. If the user starts a touch while
                // that inline style is still set, it overrides the
                // `.agp-card-carousel__track--dragging` CSS rule's
                // `transition: none`, so the boundary-rotation transform jump
                // in onTouchMove gets animated over 400ms — visually sliding
                // through every slide in between (flash of many images on
                // mobile, especially Chrome iOS). Clear it here.
                this.track.style.transition = '';
            }
        });
    }

    /**
     * AGP Infinite Scroll Custom Element
     * Observes its own visibility and fetches the next page of products,
     * appending them to the grid. Stops when no more pages.
     */
    if (!customElements.get('agp-infinite-scroll')) {
        customElements.define('agp-infinite-scroll', class extends HTMLElement {
            connectedCallback() {
                this.nextUrl = this.dataset.nextUrl;
                this.gridSelector = this.dataset.gridSelector;
                this.loading = false;
                this.manual = this.hasAttribute('data-manual');
                // When data-cols-d/m are set, override columns on new items (no layout reset)
                this.colsD = this.dataset.colsD || null;
                this.colsM = this.dataset.colsM || null;

                if (!this.nextUrl) {
                    this.classList.add('agp-infinite-scroll--done');
                    return;
                }

                if (this.manual) {
                    // Manual mode: load on button click
                    const btn = this.querySelector('.agp-infinite-scroll__button');
                    if (btn) {
                        btn.addEventListener('click', () => this.loadMore());
                    }
                } else {
                    // Auto mode: load on scroll (IntersectionObserver).
                    // rootMargin 800px so the next page starts loading well before
                    // the user reaches the bottom of the current grid.
                    this.observer = new IntersectionObserver((entries) => {
                        entries.forEach(entry => {
                            if (entry.isIntersecting && !this.loading) {
                                this.loadMore();
                            }
                        });
                    }, { rootMargin: '800px' });

                    this.observer.observe(this);

                    // Scroll fallback: IntersectionObserver can drop callbacks
                    // during very fast scrolls (when the sentinel's
                    // isIntersecting flips true→false within a single frame,
                    // the browser may only report the final state). A
                    // rAF-throttled scroll handler mirrors the IO rootMargin
                    // check so fast scrolling still triggers loadMore.
                    //
                    // IMPORTANT: trigger ONLY when the sentinel is
                    // approaching the viewport from below (or just barely
                    // past the top). If the sentinel is far above the
                    // viewport (user parked in the footer area), do NOT keep
                    // loading — browser scroll anchoring keeps the footer in
                    // view as new products are appended, which makes the
                    // sentinel's viewport position stationary and would
                    // otherwise cause runaway loading of every remaining page.
                    this._scrollScheduled = false;
                    this._onScroll = () => {
                        if (this._scrollScheduled) return;
                        this._scrollScheduled = true;
                        requestAnimationFrame(() => {
                            this._scrollScheduled = false;
                            if (!this.nextUrl || this.loading) return;
                            const rect = this.getBoundingClientRect();
                            const viewportH = window.innerHeight || document.documentElement.clientHeight;
                            // Sentinel within the IO band: approaching from
                            // below (within 800px) OR just barely past the
                            // top (within 200px). Beyond that, user has moved
                            // past the products area — stop loading.
                            if (rect.top <= viewportH + 800 && rect.top >= -200) {
                                this.loadMore();
                            }
                        });
                    };
                    window.addEventListener('scroll', this._onScroll, { passive: true });

                    // Initial visibility check: when the page is opened (or
                    // refreshed) with the scroll position already past the last
                    // products (e.g. in the footer), the sentinel may sit fully
                    // above the viewport and IntersectionObserver will not fire.
                    // Recursively load pages until the sentinel is back in view
                    // or we run out of pages.
                    requestAnimationFrame(() => this.fillUntilVisible());
                }
            }

            /**
             * One-shot fill on mount: if the page opens with the scroll
             * position already past the last products (sentinel at/above the
             * viewport bottom), load one page so the user has products to
             * scroll back into. We do NOT recurse — with browser scroll
             * anchoring the sentinel's viewport position stays put as we
             * append, and recursing would load every remaining page. The
             * scroll fallback above will load further pages if the user
             * actually scrolls toward the products area.
             */
            async fillUntilVisible() {
                if (!this.nextUrl || this.loading) return;
                const rect = this.getBoundingClientRect();
                const viewportH = window.innerHeight || document.documentElement.clientHeight;
                // Sentinel still below the viewport: nothing to do.
                if (rect.top > viewportH) return;
                await this.loadMore();
            }

            disconnectedCallback() {
                if (this.observer) this.observer.disconnect();
                if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
            }

            async loadMore() {
                if (!this.nextUrl || this.loading) return;
                this.loading = true;
                this.classList.add('agp-infinite-scroll--loading');

                try {
                    const response = await fetch(this.nextUrl);
                    if (!response.ok) throw new Error(response.status);
                    const html = await response.text();
                    const doc = new DOMParser().parseFromString(html, 'text/html');

                    // Extract new product cards from the fetched page
                    const newGrid = doc.querySelector(this.gridSelector);
                    // Check if there's another page
                    const nextEl = doc.querySelector('agp-infinite-scroll');
                    const isLastPage = !nextEl || !nextEl.dataset.nextUrl;

                    if (newGrid) {
                        const grid = document.querySelector(this.gridSelector);
                        const items = [...newGrid.children];
                        items.forEach(item => {
                            // The server already renders the correct
                            // --cols-d / --cols-m on every card based on the
                            // section's layout mode (first-row or alternating)
                            // and paginate.current_offset, so we MUST NOT
                            // override them here — doing so would force every
                            // appended card to use c_d/c_m and destroy the
                            // per-row layout (last-row trim, alternation, etc).
                            grid.appendChild(item);
                        });
                    }

                    if (!isLastPage) {
                        this.nextUrl = nextEl.dataset.nextUrl;
                    } else {
                        this.nextUrl = null;
                        this.classList.add('agp-infinite-scroll--done');
                        if (this.observer) this.observer.disconnect();
                    }
                } catch (e) {
                    console.error('[agp-infinite-scroll] Failed to load:', e);
                    // On error, stop trying
                    this.nextUrl = null;
                    this.classList.add('agp-infinite-scroll--done');
                    this.observer.disconnect();
                } finally {
                    this.loading = false;
                    this.classList.remove('agp-infinite-scroll--loading');
                }
            }
        });
    }

    /**
     * Initialize all Agapee JS features
     */
    function init() {
        initOverlayHover();
        initParallax();
        initProductCardBadges();
    }

    // Run on DOMContentLoaded
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Re-initialize on Shopify section load (for theme editor)
    document.addEventListener('shopify:section:load', init);
})();
